import React, { useEffect } from 'react'
import Breadcrum from '../../components/breadcrum'
import AddItem from '../../components/addItem'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  blockAdmin,
  deleteAdmin,
  getAllAdmin,
  unblockAdmin,
} from '../../api/adminApi'
import Swal from 'sweetalert2'
import { FaTrash } from 'react-icons/fa'
import { handleDate } from '../../handleDateAndPdf'

const Admin = () => {
  useEffect(() => {
    document.title = 'Admin Page'
  }, [])

  const queryClient = useQueryClient()

  const blockMutation = useMutation({
    mutationFn: (id) => blockAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin'])
      toast.success('Admin has been blocked')
    },
    onError: () => {
      toast.error('Failed to block admin')
    },
  })

  const unblockMutation = useMutation({
    mutationFn: (id) => unblockAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin'])
      toast.success('Admin has been unblocked')
    },
    onError: () => {
      toast.error('Failed to unblock admin')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin'])
      toast.success('Admin Deleted ! ')
    },
    onError: (error) => {
      toast.error('Failed to delete admin', error)
    },
  })

  const {
    data: AllAdmin = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['admin'],
    queryFn: getAllAdmin,
    isError: (error) => {
      toast.error('error while fetching data:', error)
    },
  })

  if (isLoading) {
    return (
      <>
        <Breadcrum subheading="All admin" heading="admin" />
        <div>loading data...</div>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Breadcrum subheading="All admin" heading="admin" />
        <div>Error while fetching admin data</div>
      </>
    )
  }

  const handleStatusToggle = async (e, admin) => {
    const isChecked = e.target.checked

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: isChecked
        ? `You are about to block ${admin.fullName}`
        : `You are about to unblock ${admin.fullName}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    })

    if (result.isConfirmed) {
      if (isChecked) {
        blockMutation.mutate(admin.id)
      } else {
        unblockMutation.mutate(admin.id)
      }
    } else {
      // Revert checkbox state manually if cancelled
      e.target.checked = !isChecked
    }
  }

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to Delete Admin',
      icon: 'danger',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    })

    if (result.isConfirmed) {
      deleteMutation.mutate(id)
    }
  }

  return (
    <>
      <Breadcrum subheading="All admin" heading="admin" />
      <h2 className="md:w-[90%] text-left mx-auto text-gray-600 text-2xl font-aptos-semibold">
        Admin
      </h2>
      <div className="md:w-[90%] mx-auto my-6 overflow-x-auto whitespace-nowrap">
        <table className="min-w-full border border-gray-200 text-center shadow-md rounded-lg">
          <thead className="bg-[#E9ECEF] text-gray-700">
            <tr>
              <th className="py-3 px-4 border-b">S.No</th>
              <th className="py-3 px-4 border-b">Full Name</th>
              <th className="py-3 px-4 border-b">Email</th>
              <th className="py-3 px-4 border-b">Role</th>
              <th className="py-3 px-4 border-b">Last Login</th>
              <th className="py-3 px-4 border-b">Creaetd At</th>
              <th className="py-3 px-4 border-b">Blocked At</th>
              <th className="py-3 px-4 border-b">Unblocked At</th>
              <th className="py-3 px-4 border-b">Deleted At</th>
              <th className="py-3 px-4 border-b">Status</th>
              <th className="py-3 px-4 border-b">Permission</th>
              <th className="py-3 px-4 border-b">Block/Unblock</th>
              <th className="py-3 px-4 border-b">Delete</th>
            </tr>
          </thead>
          <tbody>
            {AllAdmin.content.map((item) => (
              <tr
                key={item.id}
                className="hover:bg-gray-50 text-gray-700 transition-colors duration-200"
              >
                <td className="p-3">{item.id}</td>
                <td className="py-3 px-4 border-b capitalize">
                  {item.fullName}
                </td>
                <td className="py-3 px-4 border-b">{item.username}</td>

                <td className="py-3 px-4 border-b">{item.role}</td>
                <td className="py-3 px-4 border-b ">
                  {handleDate(item.lastLogin)}
                </td>
                <td className="py-3 px-4 border-b ">
                  {handleDate(item.createdAt)}
                </td>
                <td className="py-3 px-4 border-b ">
                  {handleDate(item.blockedAt)}
                </td>
                <td className="py-3 px-4 border-b ">
                  {handleDate(item.unblockedAt)}
                </td>
                <td className="py-3 px-4 border-b ">
                  {handleDate(item.deletedAt)}
                </td>
                <td className="py-3 px-4 border-b ">{item.status}</td>
                <td className="py-3 px-4 border-b">
                  <div className="flex justify-center space-x-2">
                    <Link
                      to={`/dealdashboard/add/admin/${item.id}`}
                      className="bg-orange-400 text-white p-2 rounded-md hover:bg-orange-500"
                      title="see"
                    >
                      See Permission
                    </Link>
                  </div>
                </td>
                <td className="py-3 px-4 border-b ">
                  <input
                    type="checkbox"
                    className="cursor-pointer"
                    checked={item.status === 'BLOCKED'} // or based on your actual status flag
                    onChange={(e) => handleStatusToggle(e, item)}
                  />
                </td>
                <td className="py-3 px-4 border-b">
                  <div
                    className="flex justify-center items-center text-red-600 cursor-pointer hover:text-red-800 transition"
                    onClick={() => handleDelete(item.id)}
                  >
                    <FaTrash size={18} title="Delete" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AddItem redirect="/dealdashboard/add/admin" />
    </>
  )
}

export default Admin
